import React, { forwardRef, useEffect, useState } from "react";
import { Dialog as MuiDialog, Fade, IconButton } from "@mui/material";
import {
  CloseRounded,
  DescriptionOutlined,
  PrintRounded,
  ReceiptLongOutlined,
  CheckRounded,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import Button from "./Button";
import { RECEIPT_FORMAT } from "../utils/receiptFormats";

const DialogTransition = forwardRef(function DialogTransition(props, ref) {
  return <Fade ref={ref} {...props} timeout={220} />;
});

// "Which receipt does this customer want?" — shown after a sale or a return when
// Outlet > Settings has AskReceiptFormatOnSale on. Shops running a thermal roll
// and an A4 printer side by side pick at the counter, not in configuration.
//
// Deliberately built on MUI's Dialog rather than layout/Dialog: every POS caller
// of that one passes `onClose={false}` with no `setShow`, which kills Escape, the
// backdrop AND its own X — the modal trap that once left a cashier unable to reach
// the menu or even log out. Here `onClose` is wired to every exit, and the caller
// treats "closed by any means" as "finish the sale without printing".
//
// The dialog stays open after a print so the same sale can go out on two printers
// (a thermal slip for the till roll, an A4 invoice for the customer's records).
//
// No single-key shortcuts on purpose: layout/TopNav/GlobalSearch binds a global
// bare "/" and a barcode scanner firing mid-dialog would type straight into it.
const ReceiptFormatDialog = ({
  open,
  formats = [],
  preferred,
  onPrint,
  onClose,
}) => {
  const { t } = useTranslation(["common", "forms"]);

  // Which formats have already gone to a printer this time round. keepMounted
  // means this component survives between sales, so it must reset on open or the
  // previous customer's ticks would show against a fresh sale.
  const [printed, setPrinted] = useState([]);

  useEffect(() => {
    if (open) setPrinted([]);
  }, [open]);

  const handlePrint = (format) => {
    onPrint(format);
    setPrinted((j) => (j.includes(format) ? j : [...j, format]));
  };

  const meta = {
    [RECEIPT_FORMAT.thermal]: {
      label: t("common:receiptFormatThermal", "Thermal"),
      icon: <ReceiptLongOutlined sx={{ fontSize: 26 }} />,
    },
    [RECEIPT_FORMAT.a4]: {
      label: t("common:receiptFormatA4", "A4"),
      icon: <DescriptionOutlined sx={{ fontSize: 26 }} />,
    },
    [RECEIPT_FORMAT.a5]: {
      label: t("common:receiptFormatA5", "A5"),
      icon: <DescriptionOutlined sx={{ fontSize: 22 }} />,
    },
  };

  return (
    <MuiDialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      TransitionComponent={DialogTransition}
      keepMounted
      PaperProps={{
        sx: {
          borderRadius: "14px",
          overflow: "hidden",
          boxShadow: "0 16px 48px -12px rgba(15, 23, 42, 0.22)",
          maxWidth: 460,
          m: 2,
        },
      }}
    >
      {/* -------- Header -------- */}
      <div className="flex items-center justify-between px-3 py-[10px] border-b border-gray-100">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-7 h-7 rounded-[8px] bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
            <PrintRounded sx={{ fontSize: 16 }} />
          </div>
          <div className="text-[13px] font-semibold text-gray-800 leading-tight truncate">
            {t("common:chooseReceiptFormat", "Print receipt")}
          </div>
        </div>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{
            color: "#9ca3af",
            "&:hover": { backgroundColor: "#f3f4f6", color: "#4b5563" },
          }}
        >
          <CloseRounded sx={{ fontSize: 18 }} />
        </IconButton>
      </div>

      {/* -------- Format buttons -------- */}
      <div className="px-3 py-4 bg-white">
        <div
          className={`grid gap-3 ${
            formats.length > 2 ? "grid-cols-3" : "grid-cols-2"
          }`}
        >
          {formats.map((format) => {
            const isPrinted = printed.includes(format);
            return (
              <Button
                key={format}
                onClick={() => handlePrint(format)}
                variant={format === preferred && !isPrinted ? "contained" : "outlined"}
                className="w-full !p-2 md:!p-3 transition-colors"
                sx={{ minHeight: 92, borderRadius: "12px" }}
              >
                <div className="grid justify-items-center gap-1">
                  {isPrinted ? <CheckRounded sx={{ fontSize: 26 }} /> : meta[format]?.icon}
                  <span className="text-[15px] md:text-[17px] font-semibold leading-none">
                    {meta[format]?.label}
                  </span>
                  {isPrinted && (
                    <span className="text-[10px] font-normal uppercase opacity-70 leading-none">
                      {t("common:receiptPrinted", "Printed")}
                    </span>
                  )}
                </div>
              </Button>
            );
          })}
        </div>

        <p className="text-[11px] text-gray-400 mt-3 leading-relaxed text-center">
          {t(
            "common:chooseReceiptFormatHint",
            "Pick a format to print. You can print more than one."
          )}
        </p>
      </div>

      {/* -------- Footer -------- */}
      <div className="px-3 py-3 border-t border-gray-100 bg-gray-50/40">
        {/* One exit, labelled for what it means right now: nothing printed yet is
            a deliberate "no receipt"; after a print it is simply done.

            Full width and 48px tall because this is the button a cashier hits on
            every single sale, often without looking down — the same reason the
            format tiles above are oversized. A 120px button floated right is a
            small target on a counter touchscreen. */}
        <Button
          onClick={onClose}
          variant={printed.length ? "contained" : "outlined"}
          fullWidth
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: 15,
            borderRadius: "10px",
            height: 48,
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          }}
        >
          {printed.length
            ? t("common:done")
            : t("common:noReceipt", "No receipt")}
        </Button>
      </div>
    </MuiDialog>
  );
};

export default ReceiptFormatDialog;
